import { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Star,
  MapPin,
  Clock,
  Trash2,
  Eye,
  Building,
  Award,
  Globe,
  Mail,
  Copy,
} from 'lucide-react'
import { 
  yogaSchoolService, 
  YogaSchool, 
  YogaSchoolStats, 
  formatYogaSchoolStatus, 
  formatFeaturedStatus, 
  formatRelativeTime,
  getRatingColor,
  getStatusBadgeColor,
  getFeaturedBadgeColor
} from '../services/yogaSchoolService'
import SearchInput from '../components/SearchInput'
import ConfirmationDialog from '../components/ConfirmationDialog'
import AdminPagination from '../components/AdminPagination'
import toast from 'react-hot-toast'

const extractErrorMessage = (err: any, fallback: string): string => {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  )
}

const normalizeWebsiteHref = (raw: string): string => {
  const t = raw.trim()
  if (!t) return '#'
  if (/^https?:\/\//i.test(t)) return t
  return `https://${t}`
}

const displayWebsiteLabel = (raw: string): string => {
  const t = raw.trim()
  if (!t) return ''
  return t.replace(/^https?:\/\//i, '').replace(/\/$/, '') || t
}

const getOwnerEmail = (school: YogaSchool): string | null => {
  const o = school.ownerId
  if (o && typeof o === 'object' && 'email' in o && typeof (o as { email?: string }).email === 'string') {
    const e = (o as { email: string }).email.trim()
    return e || null
  }
  return null
}

const YogaSchools = () => {
  const navigate = useNavigate()
  const [schools, setSchools] = useState<YogaSchool[]>([])
  const [stats, setStats] = useState<YogaSchoolStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [featuredFilter, setFeaturedFilter] = useState('ALL')
  const [sourceFilter, setSourceFilter] = useState<'ALL' | 'YOGA_SCHOOL' | 'BOOKMYYOGATTC'>('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const pageSize = 12
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [schoolToDelete, setSchoolToDelete] = useState<YogaSchool | null>(null)
  const [showToggleModal, setShowToggleModal] = useState(false)
  const [schoolToToggle, setSchoolToToggle] = useState<YogaSchool | null>(null)
  const [showFeatureModal, setShowFeatureModal] = useState(false)
  const [schoolToFeature, setSchoolToFeature] = useState<YogaSchool | null>(null)
  const [showVerifyModal, setShowVerifyModal] = useState(false)
  const [schoolToVerify, setSchoolToVerify] = useState<YogaSchool | null>(null)
  
  // Use refs to store latest values without causing re-renders
  const searchTermRef = useRef(searchTerm)
  const statusFilterRef = useRef(statusFilter)
  const featuredFilterRef = useRef(featuredFilter)
  const sourceFilterRef = useRef(sourceFilter)
  const currentPageRef = useRef(currentPage)

  // Update refs when values change
  useEffect(() => {
    searchTermRef.current = searchTerm
  }, [searchTerm])

  useEffect(() => {
    statusFilterRef.current = statusFilter
  }, [statusFilter])

  useEffect(() => {
    featuredFilterRef.current = featuredFilter
  }, [featuredFilter])

  useEffect(() => {
    sourceFilterRef.current = sourceFilter
  }, [sourceFilter])

  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const searchParams = {
        page: currentPageRef.current,
        limit: pageSize,
        isActive: statusFilterRef.current !== 'ALL' ? statusFilterRef.current : undefined,
        isFeatured: featuredFilterRef.current !== 'ALL' ? featuredFilterRef.current : undefined,
        createdBySource:
          sourceFilterRef.current !== 'ALL' ? sourceFilterRef.current : undefined,
        search: searchTermRef.current || undefined,
        sortBy: 'createdAt' as const,
        sortOrder: 'desc' as const
      }

      const response = await yogaSchoolService.getYogaSchools(searchParams)

      if (response.success && response.data) {
        setSchools(response.data.schools)
        const p = response.data.pagination
        setTotalPages(p?.totalPages ?? p?.pages ?? 1)
        setTotalItems(p?.totalItems ?? p?.total ?? response.data.schools.length)
      }
    } catch (error: any) {
      console.error('Error fetching schools:', error)
      setError(error.message || 'Failed to fetch yoga schools')
    } finally {
      setLoading(false)
    }
  }, []) // No dependencies to avoid circular updates

  const fetchStats = useCallback(async () => {
    try {
      const response = await yogaSchoolService.getYogaSchoolStats()
      if (response.success && response.data && response.data.stats) {
        setStats(response.data.stats)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [])

  // Search input change handler
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  // Debounced search/filter effect — whenever the filter inputs change we
  // reset to page 1 (the `currentPage` effect below then fires the fetch).
  // If we were already on page 1, nothing fires, so we refetch here too.
  const isFirstFilterRender = useRef(true)
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false
      return
    }
    const timeoutId = setTimeout(() => {
      if (currentPageRef.current !== 1) {
        setCurrentPage(1)
      } else {
        fetchSchools()
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, statusFilter, featuredFilter, sourceFilter, fetchSchools])

  // Initial load + refetch on pagination change. `fetchSchools` itself is
  // stable (empty deps) and reads the latest page from a ref, so listing
  // `currentPage` here is what makes the prev/next buttons actually fetch.
  useEffect(() => {
    fetchSchools()
  }, [fetchSchools, currentPage])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])


  const copyOwnerEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email)
      toast.success('Email copied to clipboard')
    } catch {
      toast.error('Could not copy email')
    }
  }

  const handleSchoolAction = (action: string, school: YogaSchool) => {
      switch (action) {
        case 'view':
          navigate(`/admin/yoga-schools/${school._id}`)
          break
        case 'toggle':
        setSchoolToToggle(school)
        setShowToggleModal(true)
          break
        case 'feature':
        setSchoolToFeature(school)
        setShowFeatureModal(true)
          break
        case 'verify':
        setSchoolToVerify(school)
        setShowVerifyModal(true)
          break
        case 'delete':
          setSchoolToDelete(school)
          setShowDeleteModal(true)
          break
      }
  }

  const handleToggleConfirm = async () => {
    if (!schoolToToggle) return
    const target = schoolToToggle
    setActionLoading(target._id)
    setSchools(prev => prev.map(s =>
      s._id === target._id ? { ...s, _isUpdating: true } : s
    ))
    // Always close the popup so the user is never stuck behind a modal,
    // regardless of whether the request succeeds or fails.
    setShowToggleModal(false)
    setSchoolToToggle(null)
    try {
      await yogaSchoolService.toggleYogaSchoolStatus(target._id, !target.isActive)
      await fetchSchools()
      await fetchStats()
      toast.success(target.isActive ? 'School deactivated' : 'School activated')
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to update school status')
      toast.error(message)
      setError(message)
    } finally {
      setActionLoading(null)
      setSchools(prev => prev.map(s =>
        s._id === target._id ? { ...s, _isUpdating: false } : s
      ))
    }
  }

  const handleFeatureConfirm = async () => {
    if (!schoolToFeature) return
    const target = schoolToFeature
    const wasFeatured = target.isFeatured
    setActionLoading(target._id)
    setSchools(prev => prev.map(s =>
      s._id === target._id ? { ...s, _isUpdating: true } : s
    ))
    setShowFeatureModal(false)
    setSchoolToFeature(null)
    try {
      await yogaSchoolService.toggleFeaturedStatus(target._id)
      await fetchSchools()
      await fetchStats()
      toast.success(wasFeatured ? 'Removed from featured' : 'Added to featured')
    } catch (error: any) {
      const message = extractErrorMessage(
        error,
        wasFeatured ? 'Failed to remove from featured' : 'Failed to add to featured',
      )
      toast.error(message)
      setError(message)
    } finally {
      setActionLoading(null)
      setSchools(prev => prev.map(s =>
        s._id === target._id ? { ...s, _isUpdating: false } : s
      ))
    }
  }

  const handleVerifyConfirm = async () => {
    if (!schoolToVerify) return
    const target = schoolToVerify
    setActionLoading(target._id)
    setSchools(prev => prev.map(s =>
      s._id === target._id ? { ...s, _isUpdating: true } : s
    ))
    setShowVerifyModal(false)
    setSchoolToVerify(null)
    try {
      await yogaSchoolService.verifyYogaSchool(target._id)
      await fetchSchools()
      await fetchStats()
      toast.success('School verified')
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to verify yoga school')
      toast.error(message)
      setError(message)
    } finally {
      setActionLoading(null)
      setSchools(prev => prev.map(s =>
        s._id === target._id ? { ...s, _isUpdating: false } : s
      ))
    }
  }

  const handleDeleteConfirm = async () => {
    if (!schoolToDelete) return
    const target = schoolToDelete
    setActionLoading(target._id)
    setSchools(prev => prev.map(s =>
      s._id === target._id ? { ...s, _isUpdating: true } : s
    ))
    setShowDeleteModal(false)
    setSchoolToDelete(null)
    try {
      await yogaSchoolService.deleteYogaSchool(target._id)
      await fetchSchools()
      await fetchStats()
      toast.success(`Deleted ${target.name}`)
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to delete yoga school')
      toast.error(message)
      setError(message)
    } finally {
      setActionLoading(null)
      setSchools(prev => prev.map(s =>
        s._id === target._id ? { ...s, _isUpdating: false } : s
      ))
    }
  }

  const getStatusBadge = (school: YogaSchool) => {
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(school.isActive)}`}>
        {formatYogaSchoolStatus(school.isActive)}
      </span>
    )
  }

  const getFeaturedBadge = (school: YogaSchool) => {
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFeaturedBadgeColor(school.isFeatured)}`}>
        {formatFeaturedStatus(school.isFeatured)}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Yoga Schools
            </h1>
            <p className="text-gray-600">Manage yoga schools and their verification status</p>
          </div>
                <div className="text-sm text-gray-500 font-medium bg-white px-4 py-2 rounded-lg shadow-sm border">
                {schools.length} schools
                {searchTerm || statusFilter !== 'ALL' || featuredFilter !== 'ALL' || sourceFilter !== 'ALL'
                  ? ' (filtered)'
                  : ''}
              </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { label: 'Total Schools', value: stats.totalSchools, color: 'bg-blue-500', icon: Building },
              { label: 'Active Schools', value: stats.activeSchools, color: 'bg-green-500', icon: CheckCircle },
              { label: 'Featured Schools', value: stats.featuredSchools, color: 'bg-yellow-500', icon: Award },
              { label: 'Pending Schools', value: stats.pendingSchools, color: 'bg-orange-500', icon: Clock }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {/* Search */}
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search schools by name, location..."
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              <option value="ALL">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {/* Featured Filter */}
            <select
              value={featuredFilter}
              onChange={(e) => setFeaturedFilter(e.target.value)}
              className="w-full sm:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              <option value="ALL">All Schools</option>
              <option value="true">Featured</option>
              <option value="false">Not Featured</option>
            </select>

            {/* Created-by source (admin vs self-serve) */}
            <select
              value={sourceFilter}
              onChange={(e) =>
                setSourceFilter(e.target.value as 'ALL' | 'YOGA_SCHOOL' | 'BOOKMYYOGATTC')
              }
              className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              <option value="ALL">All sources</option>
              <option value="BOOKMYYOGATTC">Bookmyyogattc onboarded</option>
              <option value="YOGA_SCHOOL">School self-created</option>
            </select>

            <button
              onClick={fetchSchools}
              disabled={loading}
              className="px-6 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50 font-medium"
              title="Refresh schools data"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

            {/* Schools Grid */}
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-300 ${
              loading ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}>
              {loading ? (
                <div className="col-span-full flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="relative mb-4">
                      <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Search className="w-5 h-5 text-amber-500 animate-pulse" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Schools</h3>
                    <p className="text-gray-500">Please wait while we fetch the latest data...</p>
                  </div>
                </div>
              ) : schools.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500">
                  {searchTerm || statusFilter !== 'ALL' || featuredFilter !== 'ALL' || sourceFilter !== 'ALL'
                    ? 'No schools match your filters'
                    : 'No yoga schools found'
                  }
                </div>
              ) : (
                schools.map((school, index) => {
                  const ownerEmail = getOwnerEmail(school)
                  return (
            <motion.div
              key={school._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`group relative bg-white rounded-xl p-6 shadow-sm border transition-all duration-200 ${
                school._isUpdating 
                  ? 'border-amber-300 bg-amber-50/30 opacity-75' 
                  : 'border-gray-200 hover:shadow-lg hover:border-amber-300'
              }`}
            >
              {/* Loading overlay for individual school */}
              {school._isUpdating && (
                <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center z-10">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-amber-600 font-medium">Updating...</span>
                  </div>
                </div>
              )}
              {/* Header */}
              <div className="mb-4">
                {/* Action Buttons - Top row */}
                <div className="flex items-center justify-end gap-2 mb-3">
                  <button
                    onClick={() => handleSchoolAction('view', school)}
                    className="p-2 text-gray-400 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleSchoolAction('toggle', school)}
                    disabled={actionLoading === school._id}
                    className={`p-2 transition-colors rounded-lg disabled:opacity-50 ${
                      school.isActive 
                        ? 'text-gray-400 hover:text-red-500 hover:bg-red-50' 
                        : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                    }`}
                    title={school.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {actionLoading === school._id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : school.isActive ? (
                      <XCircle className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSchoolAction('feature', school)}
                    disabled={actionLoading === school._id}
                    className="p-2 text-gray-400 hover:text-yellow-500 transition-colors rounded-lg hover:bg-yellow-50 disabled:opacity-50"
                    title={school.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
                  >
                    <Award className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleSchoolAction('delete', school)}
                    disabled={actionLoading === school._id}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50 disabled:opacity-50"
                    title="Delete School"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  {getStatusBadge(school)}
                  {getFeaturedBadge(school)}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {school.name}
                </h3>
                <div className="flex items-center text-sm text-gray-500">
                  <MapPin className="w-4 h-4 mr-2 text-amber-500 flex-shrink-0" />
                  <span className="line-clamp-1">{school.location}</span>
                </div>
                {school.website?.trim() && (
                  <div className="flex items-center gap-2 mt-2 min-w-0">
                    <Globe className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <a
                      href={normalizeWebsiteHref(school.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-amber-600 hover:text-amber-700 truncate"
                      title={school.website.trim()}
                    >
                      {displayWebsiteLabel(school.website)}
                    </a>
                  </div>
                )}
                {ownerEmail && (
                  <div className="flex items-center gap-2 mt-2 min-w-0">
                    <button
                      type="button"
                      onClick={() => void copyOwnerEmail(ownerEmail)}
                      className="p-1 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 flex-shrink-0 transition-colors"
                      title="Copy email"
                      aria-label="Copy owner email to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <Mail className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600 truncate flex-1 min-w-0" title={ownerEmail}>
                      {ownerEmail}
                    </span>
                  </div>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.floor(school.rating)
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <span className={`text-sm font-semibold ${getRatingColor(school.rating)}`}>
                  {school.rating}
                </span>
                <span className="text-sm text-gray-500">
                  ({school.totalReviews} reviews)
                </span>
              </div>

              {/* Key Info */}
              {school.city && school.country && (
                <div className="mb-4">
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-600">Location</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {school.city}, {school.country}
                    </span>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between text-sm text-gray-400 pt-4 border-t border-gray-100">
                <span>Created {formatRelativeTime(school.createdAt)}</span>
                <button
                  onClick={() => handleSchoolAction('view', school)}
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  View Details →
                </button>
              </div>
            </motion.div>
                  )
                })
              )
            }
      </div>

      {/* Pagination — lives outside the grid so it stretches the full width
          of the page instead of being squeezed into a single grid column. */}
      {!loading && (
        <AdminPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          disabled={loading}
          onPageChange={setCurrentPage}
          className="p-6 shadow-sm rounded-xl"
        />
      )}
    </div>

      {/* Toggle Status Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showToggleModal}
        onClose={() => {
          setShowToggleModal(false)
          setSchoolToToggle(null)
        }}
        onConfirm={handleToggleConfirm}
        title={schoolToToggle?.isActive ? 'Deactivate Yoga School' : 'Activate Yoga School'}
        message={`Are you sure you want to ${schoolToToggle?.isActive ? 'deactivate' : 'activate'} this yoga school?`}
        details={schoolToToggle ? `School: ${schoolToToggle.name}\nLocation: ${schoolToToggle.location}\n${schoolToToggle.isActive ? 'This school will be hidden from public listings and users will not be able to book courses.' : 'This school will be visible to users and they will be able to book courses.'}` : ''}
        confirmText={schoolToToggle?.isActive ? 'Deactivate' : 'Activate'}
        type={schoolToToggle?.isActive ? 'warning' : 'success'}
        isLoading={actionLoading === schoolToToggle?._id}
        icon={schoolToToggle?.isActive ? <XCircle className="w-6 h-6" /> : <CheckCircle className="w-6 h-6" />}
      />

      {/* Feature Toggle Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showFeatureModal}
        onClose={() => {
          setShowFeatureModal(false)
          setSchoolToFeature(null)
        }}
        onConfirm={handleFeatureConfirm}
        title={schoolToFeature?.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
        message={`Are you sure you want to ${schoolToFeature?.isFeatured ? 'remove this school from featured listings' : 'feature this school'}?`}
        details={schoolToFeature ? `School: ${schoolToFeature.name}\nLocation: ${schoolToFeature.location}\n${schoolToFeature.isFeatured ? 'This school will no longer appear in featured sections.' : 'This school will be highlighted in featured sections and get priority visibility.'}` : ''}
        confirmText={schoolToFeature?.isFeatured ? 'Remove Featured' : 'Add to Featured'}
        type="info"
        isLoading={actionLoading === schoolToFeature?._id}
        icon={<Award className="w-6 h-6" />}
      />

      {/* Verify Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showVerifyModal}
        onClose={() => {
          setShowVerifyModal(false)
          setSchoolToVerify(null)
        }}
        onConfirm={handleVerifyConfirm}
        title="Verify Yoga School"
        message="Are you sure you want to verify this yoga school?"
        details={schoolToVerify ? `School: ${schoolToVerify.name}\nLocation: ${schoolToVerify.location}\n\nVerifying this school will mark it as verified and allow it to be fully active on the platform.` : ''}
        confirmText="Verify School"
        type="success"
        isLoading={actionLoading === schoolToVerify?._id}
        icon={<CheckCircle className="w-6 h-6" />}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSchoolToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Yoga School"
        message={`Are you sure you want to delete this yoga school?`}
        details={schoolToDelete ? `School: ${schoolToDelete.name}\nLocation: ${schoolToDelete.location}\n\nThis action cannot be undone. All associated courses, bookings, and data will be permanently deleted.` : ''}
        confirmText="Delete School"
        type="danger"
        isLoading={actionLoading === schoolToDelete?._id}
        icon={<Trash2 className="w-6 h-6" />}
      />
    </div>
  )
}

export default YogaSchools



